// Шапка аккаунта: сводка единого профиля IEC ID (docs/INTEGRATION-GUIDE.md §9)
// и её поведение при недоступности экосистемы (§10).
//
// ─────────────────────────────────────────────────────────────────────────────
// Три случая блока Баллов, и путать их нельзя
// ─────────────────────────────────────────────────────────────────────────────
//  1. **Права нет.** По умолчанию связь несёт пять data-scopes, и
//     `points_balance` в них НЕ входит (§0.3): расширить набор может только сам
//     человек в разделе «Сервисы» витрины. Тогда `points: null` при ПУСТОМ
//     `degraded` — и это правильный ответ, а не сломанный. Блока нет, прежнее
//     значение забываем, просить его у нас бессмысленно.
//  2. **Значение есть.** Показываем как есть и запоминаем.
//  3. **`degraded` непустой** (`["points"]`) — «сейчас не смогли», а не «не
//     положено». Прежнее значение НЕ затираем пустотой: показываем его с
//     пометкой «приблизительно» (§10 п.3).
//
// **`null` — это не ноль.** Нарисованный «0 Баллов» говорит человеку, что его
// баланс сгорел, и отправляет его в поддержку выяснять, куда делись Баллы.
// Поэтому ветки «нет значения» рисуют ПУСТОТУ, а не ноль.
//
// Сервер уже отдаёт `points: null`, когда значение не подтверждено или
// протухло (`modules/iec-id/queries/get-profile.ts`), поэтому память о прошлом
// значении обязана жить здесь: в ответе его больше нет.

import { useEffect, useMemo } from "react";
import { api } from "@/lib/trpc-client";
import { useAuth } from "@/providers/auth-provider";

/** Сколько живёт запомненный баланс. Дальше «приблизительно» становится ложью. */
export const POINTS_MEMORY_TTL_MS = 24 * 60 * 60 * 1000;

/** Свежесть профиля. Самый волатильный блок сводки — баланс, 60 с (§9.3). */
const PROFILE_STALE_TIME_MS = 60_000;

const POINTS_MEMORY_KEY = "studyhub:iec-points";

export interface PointsMemory {
	/** Чей это баланс. Без владельца запись после смены аккаунта показала бы чужое. */
	userId: string;
	balance: number;
	capturedAt: number;
}

export interface HeaderPoints {
	balance: number;
	/** Значение из памяти, источник сейчас недоступен — помечаем приблизительным. */
	approximate: boolean;
}

export interface ResolvePointsInput {
	userId: string | null;
	/** `null` = ответа ещё/уже нет (загрузка, ошибка) — память не трогаем. */
	summary: { points: { balance: number } | null; degraded: readonly string[] } | null;
	memory: PointsMemory | null;
	now: number;
}

export interface ResolvePointsResult {
	points: HeaderPoints | null;
	/** Что запомнить. `null` = забыть (права нет). */
	memory: PointsMemory | null;
}

/** Память годна, только если она про ЭТОГО человека и не старше суток. */
function usableMemory(memory: PointsMemory | null, userId: string | null, now: number): boolean {
	if (!memory || !userId || memory.userId !== userId) return false;
	return now - memory.capturedAt <= POINTS_MEMORY_TTL_MS;
}

/**
 * Правило блока Баллов целиком — чистой функцией, потому что три его случая
 * различаются одним полем ответа и перепутать их проще, чем кажется.
 */
export function resolveHeaderPoints(input: ResolvePointsInput): ResolvePointsResult {
	const { userId, summary, memory, now } = input;

	// Ответа нет вовсе (первая загрузка, сеть). Это НЕ «права нет»: забывать
	// значение здесь означало бы терять его на каждом холодном старте.
	if (!summary) {
		return {
			points: usableMemory(memory, userId, now)
				? { balance: (memory as PointsMemory).balance, approximate: true }
				: null,
			memory,
		};
	}

	// Случай 2: значение подтверждено.
	if (summary.points) {
		const balance = summary.points.balance;
		return {
			points: { balance, approximate: false },
			memory: userId ? { userId, balance, capturedAt: now } : null,
		};
	}

	// Случай 3: «сейчас не смогли» — прежнее значение живо.
	if (summary.degraded.includes("points")) {
		return {
			points: usableMemory(memory, userId, now)
				? { balance: (memory as PointsMemory).balance, approximate: true }
				: null,
			memory,
		};
	}

	// Случай 1: права нет — блока нет и помнить нечего.
	return { points: null, memory: null };
}

export function readPointsMemory(): PointsMemory | null {
	try {
		const raw = window.localStorage.getItem(POINTS_MEMORY_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<PointsMemory> | null;
		if (
			!parsed ||
			typeof parsed.userId !== "string" ||
			typeof parsed.balance !== "number" ||
			typeof parsed.capturedAt !== "number"
		) {
			return null;
		}
		return { userId: parsed.userId, balance: parsed.balance, capturedAt: parsed.capturedAt };
	} catch {
		// Приватный режим / битое значение — просто нет памяти.
		return null;
	}
}

function writePointsMemory(memory: PointsMemory | null): void {
	try {
		if (memory) window.localStorage.setItem(POINTS_MEMORY_KEY, JSON.stringify(memory));
		else window.localStorage.removeItem(POINTS_MEMORY_KEY);
	} catch {
		/* приватный режим */
	}
}

export interface IecProfileHeader {
	/** Имя из единого профиля. `null` — берите своё зеркало. */
	displayName: string | null;
	avatarUrl: string | null;
	/** ТОЛЬКО маска: полной почты этот источник не отдаёт никогда (§9.2). */
	maskedEmail: string | null;
	/** `null` = блока Баллов нет. Ноль вместо `null` рисовать запрещено. */
	points: HeaderPoints | null;
	/**
	 * Уровень ЭКОСИСТЕМЫ для бейджа IEC+. Решение о доступе по нему принимать
	 * нельзя — для этого есть тариф из сессии (`serviceTier`, §4.4).
	 */
	planTier: string | null;
	isLoading: boolean;
}

/**
 * Сводка профиля для шапки.
 *
 * Запрос гейтится `isAuthenticated` и, как правило, ещё и открытием меню:
 * фонового опроса быть не должно — сводка нужна ровно в тот момент, когда на
 * неё смотрят. Ретраев минимум: недоступность экосистемы не имеет права
 * превращаться в очередь запросов из шапки (§10).
 */
export function useIecProfile(options: { enabled?: boolean } = {}): IecProfileHeader {
	const { isAuthenticated, user } = useAuth();
	const userId = user?.id ?? null;
	const enabled = (options.enabled ?? true) && isAuthenticated;

	const query = api.iecId.profile.get.useQuery(undefined, {
		enabled,
		staleTime: PROFILE_STALE_TIME_MS,
		retry: 1,
		refetchOnWindowFocus: false,
	});

	const data = query.data;
	const resolved = useMemo(
		() =>
			resolveHeaderPoints({
				userId,
				summary: data ? { points: data.points, degraded: data.degraded } : null,
				memory: readPointsMemory(),
				now: Date.now(),
			}),
		[data, userId],
	);

	useEffect(() => {
		writePointsMemory(resolved.memory);
	}, [resolved.memory]);

	return {
		displayName: data?.displayName ?? null,
		avatarUrl: data?.avatarUrl ?? null,
		maskedEmail: data?.maskedEmail ?? null,
		points: resolved.points,
		planTier: data?.plan?.tier ?? null,
		isLoading: enabled && query.isPending,
	};
}
